import { inject, Injectable, Provider } from '@angular/core';
import {
  GuestOrderCheckoutCommandResult, GuestOrderDto, OfferPreviewCancelCommandResult, OfferPreviewCheckoutCommandResult, OfferPreviewGetQueryResult,
  OrderPreviewCancelCommandResult, OrderPreviewCheckoutCommandResult, OrderPreviewGetQueryResult, ShoppingCartInquiryCheckoutCommandResult, ShoppingCartInquiryDto,
} from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { firstValueFrom, Observable } from 'rxjs';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { LoggerService } from '../../infrastructure/logger/logger.service';
import { CheckoutType } from 'projects/shared/lib/enum/checkout/checkout-type.enum';
import { ProgressStep } from '../../../components/shared/base/progress-stepper/step/progress-step.model';
import { ICheckoutService, ICheckoutServiceToken } from './checkout-service.interface';
import { IOrderPreviewApiService, IOrderPreviewApiServiceToken } from '../../api/order-preview/order-preview-api-service.interface';
import { IOfferPreviewApiService, IOfferPreviewApiServiceToken } from '../../api/offer-preview/offer-preview-api-service.interface';
import { ServiceLocator } from '../../infrastructure/service-locator.service';
import { IGuestOrderApiService, IGuestOrderApiServiceToken } from '../../api/guest-order/guest-order-api-service.interface';
import { IShoppingCartInquiryApiService, IShoppingCartInquiryApiServiceToken } from '../../api/shopping-cart-inquiry/shopping-cart-inquiry-api-service.interface';
import { IConfigurationServiceToken } from '../../infrastructure/configuration/configuration-service.interface';

@Injectable({
  providedIn: 'root'
})
export class CheckoutService implements ICheckoutService {
  private readonly logger: LoggerService;
  private readonly orderPreviewApiService: IOrderPreviewApiService | null;
  private readonly offerPreviewApiService: IOfferPreviewApiService | null;
  private readonly guestOrderApiService: IGuestOrderApiService | null;
  private readonly shoppingCartInquiryApiService: IShoppingCartInquiryApiService | null;

  private shoppingCartInquiry: ShoppingCartInquiryDto | undefined;
  private guestOrder: GuestOrderDto | undefined;

  private isOrderCheckoutAllowed: boolean;
  private isOfferCheckoutAllowed: boolean;
  private isGuestOrderCheckoutAllowed: boolean;
  private isCartInquiryCheckoutAllowed: boolean;
  private checkoutIsInProgress: boolean;
  private checkoutCleanupIsRequired: boolean;

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);
    this.orderPreviewApiService = inject(IOrderPreviewApiServiceToken, { optional: true });
    this.offerPreviewApiService = inject(IOfferPreviewApiServiceToken, { optional: true });
    this.guestOrderApiService = inject(IGuestOrderApiServiceToken, { optional: true });
    this.shoppingCartInquiryApiService = inject(IShoppingCartInquiryApiServiceToken, { optional: true });

    this.isOrderCheckoutAllowed = ENVIRONMENT.checkout.isOrderCheckoutAllowed;
    this.isOfferCheckoutAllowed = ENVIRONMENT.checkout.isOfferCheckoutAllowed;
    this.isGuestOrderCheckoutAllowed = ENVIRONMENT.checkout.isGuestOrderCheckoutAllowed;
    this.isCartInquiryCheckoutAllowed = ENVIRONMENT.checkout.isCartInquiryCheckoutAllowed;
    ServiceLocator.injector.get(IConfigurationServiceToken).checkoutConfigurationGet().subscribe((checkoutConfiguration) => {
      if (checkoutConfiguration.isOrderCheckoutAllowed != undefined) {
        this.isOrderCheckoutAllowed = checkoutConfiguration.isOrderCheckoutAllowed;
      }
      if (checkoutConfiguration.isOfferCheckoutAllowed != undefined) {
        this.isOfferCheckoutAllowed = checkoutConfiguration.isOfferCheckoutAllowed;
      }
      if (checkoutConfiguration.isGuestOrderCheckoutAllowed != undefined) {
        this.isGuestOrderCheckoutAllowed = checkoutConfiguration.isGuestOrderCheckoutAllowed;
      }
      if (checkoutConfiguration.isCartInquiryCheckoutAllowed != undefined) {
        this.isCartInquiryCheckoutAllowed = checkoutConfiguration.isCartInquiryCheckoutAllowed;
      }
    });
    this.checkoutIsInProgress = false;
    this.checkoutCleanupIsRequired = false;
  }

  public orderProgressStepArrayGet(checkoutType: CheckoutType): ProgressStep[] {
    let stepArray: ProgressStep[] = [];

    let step1 = new ProgressStep();
    step1.id = 1;
    step1.name = $localize`:@@OrderServiceStepNameShoppingCart:Shopping Cart`;
    step1.iconClassString = 'bi-cart4';
    step1.routerLink = '/shopping-cart';

    let step2 = new ProgressStep();
    step2.id = 2;
    step2.iconClassString = 'bi-signpost-split';

    let step3 = new ProgressStep();
    step3.id = 3;
    step3.iconClassString = 'bi-card-checklist';

    let step4 = new ProgressStep();
    step4.id = 4;
    step4.iconClassString = 'bi-check-lg';

    switch (checkoutType) {
      case CheckoutType.Order:
        step2.name = $localize`:@@OrderServiceStepNameOrderData:Order Data`;
        step3.name = $localize`:@@OrderServiceStepNameOrderOverview:Order Overview`;
        step4.name = $localize`:@@OrderServiceStepNameOrderFinal:Order Final`;
        step2.routerLink = '../order-data';
        break;
      case CheckoutType.Offer:
        step2.name = $localize`:@@OrderServiceStepNameOfferData:Offer Data`;
        step3.name = $localize`:@@OrderServiceStepNameOfferOverview:Offer Overview`;
        step4.name = $localize`:@@OrderServiceStepNameOfferFinal:Offer Final`;
        step2.routerLink = '../offer-data';
        break;
      case CheckoutType.GuestOrder:
        step2.name = $localize`:@@OrderServiceStepNameGuestOrderData:Guest Order Data`;
        step3.name = $localize`:@@OrderServiceStepNameGuestOrderOverview:Guest Order Overview`;
        step4.name = $localize`:@@OrderServiceStepNameGuestOrderFinal:Guest Order Final`;
        step2.routerLink = '../guest-order-data';
        break;
      case CheckoutType.ShoppingCartInquiry:
        step2.name = $localize`:@@OrderServiceStepNameShoppingCartInquiryData:Shoppingcart Inquiry Data`;
        step3.name = $localize`:@@OrderServiceStepNameShoppingCartInquiryOverview:Shoppingcart Inquiry Overview`;
        step4.name = $localize`:@@OrderServiceStepNameShoppingCartInquiryFinal:Shoppingcart Inquiry Final`;
        step2.routerLink = '../shoppingcartinquiry-data';
        break;
    }

    stepArray.push(step1);
    stepArray.push(step2);
    stepArray.push(step3);
    stepArray.push(step4);

    return stepArray;
  }

  public checkoutIsInProgressGet(): boolean {
    return this.checkoutIsInProgress;
  }

  public checkoutIsInProgressSet(inProgress: boolean) {
    this.checkoutIsInProgress = inProgress;
  }

  public checkoutCleanupIsRequiredGet() {
    return this.checkoutCleanupIsRequired;
  }

  public checkoutCleanupIsRequiredSet(isRequired: boolean) {
    this.checkoutCleanupIsRequired = isRequired;
  }

  public async orderPreviewGetAsync(
    cartNumber: number,
    cartValidate: boolean | undefined = true,
    belegDidToAdd?: string[] | undefined
  ): Promise<OrderPreviewGetQueryResult> {
    if (!this.isOrderCheckoutAllowed) {
      this.logger.error("Tried to prepare an order in checkout service while order is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.orderPreviewApiService == null) {
      this.logger.error("Tried to prepare an order in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    this.checkoutCleanupIsRequired = true; // move this to the success case from the API call.
    return firstValueFrom(this.orderPreviewApiService.orderPreviewGet(cartNumber, cartValidate, belegDidToAdd));
  }

  public async orderPreviewCheckoutAsync(
    cartNumber: number,
    belegDidToAdd?: string[] | undefined,
    sendConfirmationCopyToCustomer?: boolean,
    contactCustomer?: boolean | undefined,
    contactCustomerText?: string | undefined
  ): Promise<OrderPreviewCheckoutCommandResult> {
    if (!this.isOrderCheckoutAllowed) {
      this.logger.error("Tried to perform an order in checkout service while order is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.orderPreviewApiService == null) {
      this.logger.error("Tried to prepare an order in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    return firstValueFrom(this.orderPreviewApiService.orderPreviewCheckout(
      cartNumber,
      belegDidToAdd,
      sendConfirmationCopyToCustomer,
      contactCustomer,
      contactCustomerText
    )).then((result) => {
      this.checkoutIsInProgress = false;
      this.checkoutCleanupIsRequired = false;
      return result;
    });
  }

  public async offerPreviewGetAsync(
    cartNumber: number,
    cartValidate: boolean | undefined = true
  ): Promise<OfferPreviewGetQueryResult> {
    if (!this.isOfferCheckoutAllowed) {
      this.logger.error("Tried to prepare an offer in checkout service while offer is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.offerPreviewApiService == null) {
      this.logger.error("Tried to perform an offer in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    this.checkoutCleanupIsRequired = true; // move this to the success case from the API call.
    return firstValueFrom(this.offerPreviewApiService.offerPreviewGet(cartNumber, cartValidate)).then((result) => {
      return result;
    });
  }

  public async offerPreviewCheckoutAsync(cartNumber: number): Promise<OfferPreviewCheckoutCommandResult> {
    if (!this.isOfferCheckoutAllowed) {
      this.logger.error("Tried to perform an offer in checkout service while offer is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.offerPreviewApiService == null) {
      this.logger.error("Tried to perform an offer in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    this.checkoutIsInProgress = false; // move this to the success case from the API call.
    this.checkoutCleanupIsRequired = false; // move this to the success case from the API call.
    return await firstValueFrom(this.offerPreviewApiService.offerPreviewCheckout(cartNumber));
  }

  public guestOrderGetOrCreate(): GuestOrderDto {
    if (!this.isGuestOrderCheckoutAllowed) {
      this.logger.error("Tried to prepare a guest order in checkout service while guest order is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (!this.guestOrder) {
      this.guestOrder = new GuestOrderDto();
    }

    return this.guestOrder;
  }

  public guestOrderGet(): GuestOrderDto | undefined {
    return this.guestOrder;
  }

  public guestOrderSet(guestOrder: GuestOrderDto | undefined): boolean {
    this.guestOrder = guestOrder;
    return true;
  }

  public async guestOrderCheckoutAsync(guestOrder?: GuestOrderDto | undefined): Promise<GuestOrderCheckoutCommandResult> {
    if (!this.isGuestOrderCheckoutAllowed) {
      this.logger.error("Tried to perform a guest order in checkout service while guest order is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.guestOrderApiService == null) {
      this.logger.error("Tried to perform a guest order in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    this.checkoutIsInProgress = false;
    return await firstValueFrom(this.guestOrderApiService.guestOrderCheckout(guestOrder)).then((result) => {
      this.checkoutIsInProgress = false;
      return result;
    });
  }

  public shoppingCartInquiryGetOrCreate(): ShoppingCartInquiryDto {
    if (!this.isCartInquiryCheckoutAllowed) {
      this.logger.error("Tried to prepare a shopping cart inquiry in checkout service while shopping cart inquiry is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (!this.shoppingCartInquiry) {
      this.shoppingCartInquiry = new ShoppingCartInquiryDto();
    }
    return this.shoppingCartInquiry;
  }

  public shoppingCartInquiryGet(): ShoppingCartInquiryDto | undefined {
    return this.shoppingCartInquiry;
  }

  public shoppingCartInquirySet(shoppingCartInquiry: ShoppingCartInquiryDto | undefined) {
    this.shoppingCartInquiry = shoppingCartInquiry;
  }

  public async shoppingCartInquiryCheckoutAsync(shoppingCartInquiry?: ShoppingCartInquiryDto | undefined): Promise<ShoppingCartInquiryCheckoutCommandResult> {
    if (!this.isCartInquiryCheckoutAllowed) {
      this.logger.error("Tried to perform a shopping cart inquiry in checkout service while shopping cart inquiry is disabled.", this.constructor.name);
      throw new Error("Illegal access");
    }
    if (this.shoppingCartInquiryApiService == null) {
      this.logger.error("Tried to perform a shopping cart inquiry in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    this.checkoutIsInProgress = false;
    return await firstValueFrom(this.shoppingCartInquiryApiService.shoppingCartInquiryCheckout(shoppingCartInquiry)).then((result) => {
      this.checkoutIsInProgress = false;
      return result;
    });
  }

  public orderPreviewCancel(cartNumber: number, belegDidToAdd?: string[] | undefined): Observable<OrderPreviewCancelCommandResult> {
    this.checkoutCleanupIsRequired = false;
    if (this.orderPreviewApiService == null) {
      this.logger.error("Tried to perform an order cancel in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    return this.orderPreviewApiService.orderPreviewCancel(cartNumber, belegDidToAdd);
  }

  public offerPreviewCancel(cartNumber: number): Observable<OfferPreviewCancelCommandResult> {
    this.checkoutCleanupIsRequired = false;
    if (this.offerPreviewApiService == null) {
      this.logger.error("Tried to perform an offer cancel in checkout service while the api service is not provided.", this.constructor.name);
      throw new Error("Missing service");
    }
    return this.offerPreviewApiService.offerPreviewCancel(cartNumber);
  }
}

/**
 * Prepared provider with the token to implementation mapping.
 * Reference this at the desired provider list to enable the injection of this implementation by referencing the token(interface).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const CheckoutServiceProvider: Provider[] = [
  { provide: ICheckoutServiceToken, useExisting: CheckoutService }
];
